import React, { useCallback, useEffect, useState } from 'react';
import { useConnectionObjectApi } from '../Services/ConnectionObjectApiProvider';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Table from '../Components/Tables';
import Button from '../Components/Buttons';
import { formatDateTimeLocal } from '../utils/formatDate';
import useLatestRequest from '../Hooks/useLatestRequest';
import useTablePagination from '../Hooks/useTablePagination';
import { formatTableTitle } from '../utils/formatTitle';
import SearchInput from '../Components/SearchInput';
import { useConfirmation } from '../Components/ConfirmationContext';
import { useAlert } from '../Components/AlertContext';

function ConnectionObjectsList({ connectionName, connectionType }) {
  const {
    getObjectsByConnectionId,
    deleteConnectionObject,
    searchConnectionObjects,
    synchronizeMcpTools,
  } = useConnectionObjectApi();
  const { organizationId, connectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showConfirmation } = useConfirmation();
  const { showAlert } = useAlert();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [objects, setObjects] = useState([]);
  const [totalCountObjects, setTotalCountObjects] = useState(0);

  const tableTitle = 'Connection Objects List';
  const formattedTableTitle = formatTableTitle(tableTitle);
  const { initialPageSize, resetToggle, storedSkip, storedLimit, pageForFilters } =
    useTablePagination(tableTitle);
  const beginRequest = useLatestRequest();

  const searchStorageKey = `${formattedTableTitle}_search`;
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem(searchStorageKey) || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    () => localStorage.getItem(searchStorageKey) || '',
  );

  const columns = [
    { name: 'ID', selector: (row) => row.id },
    { name: 'Name', selector: (row) => row.name },
    { name: 'Type', selector: (row) => row.type },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive') },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at) },
  ];

  useEffect(() => {
    const handler = setTimeout(() => {
      const storedSearch = localStorage.getItem(searchStorageKey);

      if (storedSearch === searchTerm) {
        return;
      }

      setDebouncedSearchTerm(searchTerm);
      localStorage.setItem(searchStorageKey, searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm, searchStorageKey]);

  const fetchObjects = useCallback(
    async (skip, limit, search_query = '') => {
      const isLatest = beginRequest();
      try {
        setLoading(true);
        let response;
        if (search_query) {
          response = await searchConnectionObjects(search_query, skip, limit, connectionId);
        } else {
          response = await getObjectsByConnectionId(connectionId, skip, limit);
        }
        if (!isLatest()) return;
        setObjects(response.items);
        setTotalCountObjects(response.totalCount);
      } catch (err) {
        if (!isLatest()) return;
        setError('Failed to load Connection Objects');
      } finally {
        if (isLatest()) setLoading(false);
      }
    },
    [connectionId, getObjectsByConnectionId, searchConnectionObjects, beginRequest],
  );

  useEffect(() => {
    const { skip, limit } = pageForFilters();
    fetchObjects(skip, limit, debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchObjects, pageForFilters]);

  const handleTableChange = useCallback(
    (skip, limit) => {
      fetchObjects(skip, limit, debouncedSearchTerm);
    },
    [fetchObjects, debouncedSearchTerm],
  );

  const handleRowClick = (row) => {
    navigate(`/organizations/${organizationId}/connections/${connectionId}/objects/${row.id}`, {
      state: { from: location.pathname, activeTab: 'view-objects' },
    });
  };

  const handleCreateClick = () => {
    navigate(`/organizations/${organizationId}/connections/${connectionId}/objects/new`, {
      state: { from: location.pathname, activeTab: 'view-objects' },
    });
  };

  const handleDelete = (selectedId) => {
    if (!selectedId) return;
    showConfirmation('Are you sure you want to delete this connection object?', async () => {
      try {
        setLoading(true);
        await deleteConnectionObject(selectedId);
        showAlert('success', 'Connection object deleted successfully.');
        fetchObjects(storedSkip(), storedLimit(), debouncedSearchTerm);
      } catch (error) {
        showAlert('fail', 'Failed to delete the connection object.');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleSynchronizeTools = () => {
    showConfirmation('Are you sure you want to synchronize the tools?', async () => {
      try {
        setLoading(true);

        await synchronizeMcpTools(connectionId);

        showAlert('success', 'Tools have been synchronized successfully.');
        fetchObjects(storedSkip(), storedLimit(), debouncedSearchTerm);
      } catch (error) {
        showAlert('fail', 'Failed to synchronize the tools.');
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <>
      {error ? (
        <div>Error: {error}</div>
      ) : (
        <>
          <div className="mb-4">
            <SearchInput
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search connection objects..."
            />
          </div>
          <Table
            title={tableTitle}
            columns={columns}
            data={objects}
            onRowClicked={handleRowClick}
            onDelete={handleDelete}
            selectableRows
            selectableRowsHighlight
            progressPending={loading}
            pagination
            paginationServer
            paginationPerPage={initialPageSize}
            paginationTotalRows={totalCountObjects}
            onChangePage={handleTableChange}
            resetPaginationToggle={resetToggle}
          />
          <div className="d-flex justify-start" style={{ marginTop: '1rem' }}>
            <Button size="large" onClick={handleCreateClick}>
              Create New Connection Object
            </Button>
            {connectionType === 'MCP' && (
              <Button size="medium" variant="danger" onClick={handleSynchronizeTools}>
                Synchronize Tools
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default ConnectionObjectsList;
